import { TripTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getRecoilStateValue, updateRecoilState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IBooking, IBookingResponse, IBookingTypeTrip, ILegTrain, ISelectedTrainLegs } from "../../Orchestrator/Types/Index";
import { IExchangeOperations } from "../../Orchestrator/Types/Interface/IBooking";

/**
 * Recovers trip directions when the backend re-labels them after POST /replace.
 *
 * Observed on roundtrip upsell: a /replace can flip a trip's `direction` field
 * such that `OUT_BOUND`/`IN_BOUND` no longer matches the trip's actual OD.
 * Anywhere downstream that filters trips by `trip.direction === OUT_BOUND` or
 * `=== IN_BOUND` (CheckoutPage segment indexing, GetPassengerListByOffer-
 * AdmissionsAction's per-direction passenger split, PrepareBookingSummary's
 * leg summary, the per-direction exchange-fee split) reads the wrong leg and
 * the payment summary loses a row or attributes the wrong fare to a direction.
 *
 * Two failure modes are recovered:
 *  - Collapse: both trips end up tagged with the same direction.
 *  - Swap: each trip's direction is swapped with the other's.
 *
 * Recovery strategy: compare each trip's overall start/end stop refs (first
 * leg's `start` + last leg's `end`) against the LAST known
 * `selectedTrainLegsAtom`'s outbound AND inbound trip-level endpoints. Each
 * trip is re-tagged independently based on which anchor it matches — no
 * blanket "flip everything non-matched" pass. Trips that match the inbound
 * anchor are tagged IN_BOUND; trips that match the outbound anchor are tagged
 * OUT_BOUND. The matcher always runs when at least one anchor is available;
 * the only no-op exits are guards for invalid/missing inputs.
 *
 * Trip IDs persist across /replace, but legIds change. Stop refs are the
 * stable signal — every trip on a given booking has the same OD pair through
 * its lifetime regardless of which offer covers it.
 *
 * No-ops when:
 *   - fewer than 2 trips
 *   - both `selectedTrainLegs.outbound` and `selectedTrainLegs.inbound` are
 *     missing (one-way booking, or no prior state)
 *   - the inbound and outbound anchors are field-equal (symmetric same-OD
 *     itineraries) — recovery cannot disambiguate which direction is which
 *   - no trip matches either anchor
 */

interface DirectionAnchor {
  start: string;
  end: string;
}

const tripOverallStops = (trip: IBookingTypeTrip): DirectionAnchor | null => {
  const legs = trip?.legs || [];
  if (legs.length === 0) return null;
  const start = legs[0]?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef;
  const end = legs[legs.length - 1]?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef;
  if (!start || !end) return null;
  return { start, end };
};

const legArrayOverallStops = (legs: ILegTrain[] | undefined): DirectionAnchor | null => {
  if (!legs || legs.length === 0) return null;
  const start = legs[0]?.fromName;
  const end = legs[legs.length - 1]?.toName;
  if (!start || !end) return null;
  return { start, end };
};

const sameAnchor = (a: DirectionAnchor | null, b: DirectionAnchor | null): boolean => {
  if (!a || !b) return false;
  return a.start === b.start && a.end === b.end;
};

export function recoverTripDirections(
  trips: IBookingTypeTrip[],
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBookingTypeTrip[] {
  if (!trips || trips.length < 2) return trips;

  const inboundAnchor = legArrayOverallStops(priorSelectedTrainLegs?.inbound);
  const outboundAnchor = legArrayOverallStops(priorSelectedTrainLegs?.outbound);
  if (!inboundAnchor && !outboundAnchor) return trips;

  // Anchors with identical {start,end} can't be used to disambiguate. Bail
  // out so we don't mis-classify (e.g. flip a correctly-tagged IN_BOUND trip
  // to OUT_BOUND just because both anchors happen to point at the same
  // stop pair on a symmetric itinerary or stale state where outbound was
  // copy-written into the inbound slot).
  if (inboundAnchor && outboundAnchor && sameAnchor(inboundAnchor, outboundAnchor)) {
    return trips;
  }

  let mutated = false;
  const next = trips.map((trip) => {
    const tripStops = tripOverallStops(trip);
    if (!tripStops) return trip;
    // Use STRICT equality against the canonical TripTypeEnum value (not
    // `isInbound`/`isOutbound`). Those helpers also accept Transcity's
    // alternate format (`"INBOUND"` / `"OUTBOUND"` without underscore) —
    // skipping the rewrite for an alt-format match would leave the booking
    // carrying a non-canonical tag that several downstream consumers
    // (e.g. PrepareBookingSummaryAction's per-direction loop uses
    // `trip.direction === TripTypeEnum.OUT_BOUND`) compare strictly. Always
    // normalize to the canonical value when an anchor matches.
    if (sameAnchor(tripStops, inboundAnchor)) {
      if (trip?.direction === TripTypeEnum.IN_BOUND) return trip;
      mutated = true;
      return { ...trip, direction: TripTypeEnum.IN_BOUND } as IBookingTypeTrip;
    }
    if (sameAnchor(tripStops, outboundAnchor)) {
      if (trip?.direction === TripTypeEnum.OUT_BOUND) return trip;
      mutated = true;
      return { ...trip, direction: TripTypeEnum.OUT_BOUND } as IBookingTypeTrip;
    }
    return trip;
  });
  return mutated ? next : trips;
}

/**
 * Booking-level convenience wrapper. Returns a shallow-cloned booking with
 * `trips` AND `exchangeOperations[].trips` recovered when any direction was
 * corrected; otherwise returns the input unchanged so reference equality
 * (and downstream useEffect deps) stays stable on no-op runs.
 *
 * Patches `exchangeOperations[].trips` because preparePaymentSummary's
 * exchange-fee + alreadyPaid split iterates those trips with `isInbound`/
 * `isOutbound` filters — a separate read path from `booking.trips`.
 */
export function recoverBookingTripDirections(
  booking: IBooking | null | undefined,
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBooking | null | undefined {
  if (!booking) return booking;

  const recoveredTrips = booking.trips
    ? recoverTripDirections(booking.trips, priorSelectedTrainLegs)
    : booking.trips;

  // Recover exchangeOperations[].trips only when something actually changed.
  // We short-circuit the .map allocation on the common no-op path so atom
  // re-renders aren't triggered by allocation churn.
  const ops = booking.exchangeOperations as IExchangeOperations[] | undefined;
  let recoveredOps: IExchangeOperations[] | undefined = ops;
  let opsMutated = false;
  if (Array.isArray(ops) && ops.length > 0) {
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const opTrips = op?.trips as IBookingTypeTrip[] | undefined;
      if (!opTrips || opTrips.length < 2) continue;
      const recovered = recoverTripDirections(opTrips, priorSelectedTrainLegs);
      if (recovered === opTrips) continue;
      if (!opsMutated) {
        recoveredOps = ops.slice();
        opsMutated = true;
      }
      recoveredOps![i] = { ...op, trips: recovered };
    }
  }

  const tripsChanged = recoveredTrips !== booking.trips;
  if (!tripsChanged && !opsMutated) return booking;

  const next: IBooking = { ...booking };
  if (tripsChanged) next.trips = recoveredTrips;
  if (opsMutated) next.exchangeOperations = recoveredOps;
  return next;
}

/**
 * Response-level convenience wrapper. Call sites that receive a fresh
 * IBookingResponse from `getBooking` (or `replaceBookedOffer`, etc.) and are
 * about to write it into `bookingResponseAtom` should route through
 * {@link storeBookingResponseAtom} which uses this internally.
 */
export function recoverBookingResponseDirections(
  response: IBookingResponse | null | undefined,
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBookingResponse | null | undefined {
  if (!response?.booking) return response;
  const recoveredBooking = recoverBookingTripDirections(response.booking, priorSelectedTrainLegs);
  if (recoveredBooking === response.booking) return response;
  return { ...response, booking: recoveredBooking as IBooking } as IBookingResponse;
}

/**
 * Atomic recover + `updateRecoilState("bookingResponseAtom", ...)` helper.
 *
 * Sites that store a freshly-fetched IBookingResponse into the atom and run
 * AFTER an upsell (`/replace`) should call this instead of a bare
 * `updateRecoilState` so the atom never carries a direction-flipped booking
 * that downstream consumers (`MultiBookingSeatingSummary`,
 * `CheckoutTripSummary`, every `booking.trips[].direction` reader) would
 * mis-render.
 *
 * In-scope callers (apply the helper):
 *   - `ApplyCheckoutUpsellAction`
 *   - `ConfirmSeatSelectionAction`
 *   - `PersistAddonsOnConfirmAction` (main + remove-all + rollback)
 *
 * Out-of-scope (NOT routed through the helper by design): voucher actions,
 * upgrade flows, change-date / quick-exchange, return-journey, shuttle
 * transfer, multibooking prebook, booking-details. Those writers exist on
 * paths that do not run /replace as part of THIS branch's fix scope. If a
 * future bug surfaces on those flows from the same gateway behavior, route
 * them through this helper at that time.
 *
 * Returns the response that was actually written to the atom so callers can
 * chain further work without re-reading. When `priorSelectedTrainLegs` is
 * provided (callers should snapshot it BEFORE awaits — `selectedTrainLegsAtom`
 * is rewritten by `prepareBookingSummaryAction → updateLegTripSummary` so
 * post-await reads can race), uses it as the recovery signal; otherwise reads
 * the atom at call time.
 */
export function storeBookingResponseAtom(
  response: IBookingResponse | null | undefined,
  priorSelectedTrainLegs?: ISelectedTrainLegs | null,
): IBookingResponse | null | undefined {
  if (!response) {
    updateRecoilState("bookingResponseAtom", response);
    return response;
  }
  const anchor = priorSelectedTrainLegs !== undefined
    ? priorSelectedTrainLegs
    : getRecoilStateValue<ISelectedTrainLegs>("selectedTrainLegsAtom");
  const recovered = recoverBookingResponseDirections(response, anchor) ?? response;
  updateRecoilState("bookingResponseAtom", recovered);
  return recovered;
}
